import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a full name from contact information
 * Returns fallback if both names are empty
 *
 * @param firstName - First name (optional)
 * @param lastName - Last name (optional)
 * @param fallback - Fallback string if no name available (default: 'Unknown')
 * @returns Formatted full name
 *
 * @example
 * formatFullName('John', 'Doe') // "John Doe"
 * formatFullName('Jane') // "Jane"
 * formatFullName(undefined, 'Smith') // "Smith"
 * formatFullName() // "Unknown"
 */
export function formatFullName(
  firstName?: string | null,
  lastName?: string | null,
  fallback: string = 'Unknown',
): string {
  return [firstName, lastName].filter(Boolean).join(' ') || fallback
}

/**
 * Format duration in hours with appropriate unit label
 *
 * @param hours - Duration in hours
 * @param format - Format type: 'short' (e.g., "2.5 hrs") or 'long' (e.g., "2.5 hours")
 * @returns Formatted duration string or null if invalid
 *
 * @example
 * formatDuration(2.5) // "2.5 hrs"
 * formatDuration(1, 'long') // "1 hour"
 * formatDuration(3, 'long') // "3 hours"
 * formatDuration(0) // null
 */
export function formatDuration(hours?: number | null, format: 'short' | 'long' = 'short'): string | null {
  if (typeof hours !== 'number' || hours <= 0) return null

  if (format === 'long') {
    return hours === 1 ? '1 hour' : `${hours} hours`
  }

  return `${hours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`
}

/**
 * Format a phone number to (XXX) XXX-XXXX format
 * Strips non-numeric characters and formats 10-digit numbers
 *
 * @param phoneNumber - Phone number string (may contain formatting characters)
 * @returns Formatted phone number in (XXX) XXX-XXXX format, or original string if invalid
 *
 * @example
 * formatPhoneNumber('5133456552') // "(513) 345-6552"
 * formatPhoneNumber('(513) 345-6552') // "(513) 345-6552"
 * formatPhoneNumber('513-345-6552') // "(513) 345-6552"
 * formatPhoneNumber('invalid') // "invalid"
 */
export function formatPhoneNumber(phoneNumber?: string | null): string {
  if (!phoneNumber) return phoneNumber || ''

  // Extract only digits
  const digits = phoneNumber.replace(/\D/g, '')

  // Format as (XXX) XXX-XXXX if we have exactly 10 digits
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  // Return original if it doesn't match expected format
  return phoneNumber
}

/**
 * Format a phone number as the user types
 * Progressively formats: (XXX) -> (XXX) XXX -> (XXX) XXX-XXXX
 *
 * @param value - Current input value
 * @returns Formatted phone number string
 *
 * @example
 * formatPhoneInput('513') // "(513"
 * formatPhoneInput('5133456') // "(513) 345-6"
 * formatPhoneInput('5133456552') // "(513) 345-6552"
 */
export function formatPhoneInput(value: string): string {
  // Remove all non-numeric characters
  const digits = value.replace(/\D/g, '')

  // Limit to 10 digits
  const limited = digits.slice(0, 10)

  // Format based on length
  if (limited.length === 0) {
    return ''
  }
  if (limited.length <= 3) {
    return `(${limited}`
  }
  if (limited.length <= 6) {
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`
  }
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`
}

/**
 * Strip phone formatting to get raw 10-digit number
 *
 * @param value - Formatted phone number
 * @returns Raw digits only
 *
 * @example
 * stripPhoneFormatting('(513) 345-6552') // "5133456552"
 */
export function stripPhoneFormatting(value: string): string {
  return value.replace(/\D/g, '')
}
